import { useEffect } from 'react';

import { type UseQueryOptions, useQuery } from '@tanstack/react-query';

import { Services } from '@/infra/services';
import { type ItemId } from '@/infra/services/catalog/model';
import { type Response } from '@/infra/services/catalog/product/get-shipments';
import { useProductStore } from '@/presenters/store/catalog/product';
import { TWO_MINUTES_IN_MILISECONDS } from '@/utils/constants/time';

interface UseShipmentsProps {
  productId: ItemId;
  zipCode: string;
}

type QueryOptions = Omit<
  UseQueryOptions<Response, Error, Response>,
  'queryKey' | 'queryFn'
>;

const useShipments = (props: UseShipmentsProps, options?: QueryOptions) => {
  const { zipCode, productId } = props;

  const setShipments = useProductStore((store) => store.setShipments);

  const result = useQuery<Response, Error, Response>({
    queryKey: ['get-shipments', productId, zipCode],
    retry: false,
    staleTime: TWO_MINUTES_IN_MILISECONDS,
    queryFn: () =>
      Services.Catalog.Product.getShipments({ productId, zipCode }),
    ...options,
  });

  useEffect(() => {
    setShipments({
      isFetching: result.isFetching,
      isFetched: result.isFetched,
      data: result.data?.data || {},
    });
  }, [result.data, result.isFetched, result.isFetching, setShipments]);

  return result;
};

export default useShipments;
